// src/components/FadeInSectionText.jsx

import React, { useState, useEffect, useRef } from 'react';

/**
 * FadeInSectionText: A reusable component that applies a fade-in and slide-up
 * animation to its children when they become visible in the viewport.
 * Elements will STAY visible after their initial fade-in.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The content to render inside the section.
 * @param {number} [props.delay=0] - Delay before the animation starts (in seconds).
 * @param {number} [props.duration=0.8] - Duration of the fade-in animation (in seconds).
 * @param {object} [props.style={}] - Additional inline styles for the main div.
 */
function FadeInSectionText({ children, delay = 0, duration = 0.8, style = {} }) {
  const domRef = useRef(null);
  // State to track if the element has intersected the viewport at least once.
  // It will remain true once set, ensuring the element stays visible.
  const [hasIntersected, setHasIntersected] = useState(false);

  useEffect(() => {
    // Create a new IntersectionObserver instance.
    // The callback function will run when the target element's visibility changes.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // If the element is currently intersecting (visible in viewport)
          // AND it hasn't intersected before (to trigger animation only once)
          if (entry.isIntersecting && !hasIntersected) {
            setHasIntersected(true); // Set state to true to trigger fade-in
            // Stop observing this element once it has intersected to save resources.
            // This also ensures the animation doesn't re-trigger or reverse.
            observer.unobserve(entry.target);
          }
        });
      },
      {
        // `threshold` defines the percentage of the target element that must be visible
        // to trigger the callback. 0.1 means 10% of the element.
        threshold: 0.1,
        // `rootMargin` expands or shrinks the viewport (root) area.
        // "0px 0px -50px 0px" means the bottom boundary of the viewport is shrunk by 50px.
        // This makes the element trigger its intersection *earlier* than if it had to
        // fully enter the visual viewport, making the animation feel more simultaneous with scroll.
        rootMargin: "0px 0px -50px 0px",
      }
    );

    // If the DOM reference exists, start observing the element.
    if (domRef.current) {
      observer.observe(domRef.current);
    }

    // Cleanup function: Disconnect the observer when the component unmounts.
    // This is important to prevent memory leaks.
    return () => {
      if (domRef.current) {
        observer.unobserve(domRef.current); // Ensure unobserved before disconnect
      }
      observer.disconnect();
    };
  }, [hasIntersected]); // Dependency array: re-run effect if hasIntersected changes.
                        // This is important for the `!hasIntersected` check to work correctly.

  return (
    <div
      ref={domRef} // Attach the ref to the DOM element to observe its visibility
      style={{
        // Apply opacity and transform based on the `hasIntersected` state.
        // If true, opacity is 1 (visible), transform is translateY(0) (normal position).
        // If false, opacity is 0 (hidden), transform is translateY(50px) (starts below).
        opacity: hasIntersected ? 1 : 0,
        transform: hasIntersected ? 'translateY(0)' : 'translateY(50px)',
        // Define the CSS transition for smooth animation.
        // It applies to both opacity and transform, with custom duration and delay.
        transition: `opacity ${duration}s ease-in-out ${delay}s, transform ${duration}s cubic-bezier(0, 0, 0.58, 1) ${delay}s`,
        height: '100%', // Ensure the div takes up its full height for correct layout
        ...style, // Allow additional inline styles to be passed in
      }}
    >
      {children} {/* Render the content passed to this component */}
    </div>
  );
}

export default FadeInSectionText;